import { useEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import type { TaskDetailProps } from "../types";
import { ProjectGlyph } from "@/components/icons";
import { Tooltip } from "@/components/ui/misc";
import { cn } from "@/lib/utils";
import { iconButton } from "./chrome";
import { StatusToggle } from "./TaskRow";

function Property({ name, prop, children }: { name: string; prop: string; children: ReactNode }) {
  if (!children) return null;
  return (
    <div data-prop={prop} className="flex min-h-8 items-center gap-2">
      <dt className="w-[72px] shrink-0 self-start pt-[7px] text-[12.5px] leading-[18px] text-muted-foreground">{name}</dt>
      <dd className="-ml-2 flex min-w-0 flex-1 items-center">{children}</dd>
    </div>
  );
}

/**
 * Linear-style issue view: title, description and checklist on the left; a stacked property list
 * on the right. Used for the right-hand panel and for sheets (palette, deep links).
 */
export function ConsoleTaskDetail({ task, mode, onClose, onToggleComplete, parts }: TaskDetailProps) {
  const ref = useRef<HTMLDivElement>(null);
  const p = parts.properties;
  const doneItems = task.checklist.filter((c) => c.done).length;

  useEffect(() => {
    if (mode === "inline") ref.current?.scrollIntoView({ block: "nearest" });
  }, [mode, task.id]);

  return (
    <div
      ref={ref}
      data-task-detail={task.id}
      data-console-detail
      role="group"
      aria-label={`Task ${task.readableId}`}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
      }}
      className={cn(
        "@container/detail flex flex-col bg-background text-foreground",
        mode === "panel" && "h-full",
        mode === "inline" && "-mx-4 my-1 border-y border-border",
      )}
    >
      <header className="flex h-11 shrink-0 items-center gap-2 border-b border-border pl-4 pr-2.5">
        <StatusToggle status={task.status} onToggle={onToggleComplete} title={task.title} className="-ml-0.5" />
        <span className="text-[12.5px] font-medium text-muted-foreground tabular">{task.readableId === "DSK-0" ? "Not synced yet" : task.readableId}</span>
        <div className="ml-auto flex items-center gap-0.5 [&_button]:size-7">
          {parts.actions}
          <span aria-hidden className="mx-1 h-4 w-px bg-border" />
          <Tooltip content="Close" shortcut="Esc">
            <button type="button" aria-label="Close detail" onClick={onClose} className={iconButton}>
              <X />
            </button>
          </Tooltip>
        </div>
      </header>

      <div className={cn("flex min-h-0 flex-1 flex-col @min-[540px]/detail:flex-row", mode === "panel" && "overflow-y-auto")}>
        <section className="min-w-0 flex-1 px-6 pb-8 pt-5">
          {parts.syncNotice}
          <div data-console-title>{parts.title}</div>
          <div data-console-notes className="mt-1.5">
            {parts.notes}
          </div>
          <div data-console-checklist className="mt-5">
            {task.checklist.length > 0 && (
              <div className="mb-1.5 flex h-7 items-center gap-2 text-[12.5px] font-medium text-muted-foreground">
                <ProjectGlyph progress={{ done: doneItems, total: task.checklist.length }} className="size-3.5 text-muted-foreground" />
                <span>Checklist</span>
                <span className="text-subtle tabular">
                  {doneItems}/{task.checklist.length}
                </span>
              </div>
            )}
            {parts.checklist}
          </div>
        </section>

        <aside
          aria-label="Properties"
          className="shrink-0 border-t border-border px-4 py-4 @min-[540px]/detail:w-[264px] @min-[540px]/detail:border-l @min-[540px]/detail:border-t-0"
        >
          <dl data-console-props className="flex flex-col gap-0.5">
            <Property name="Status" prop="status">{p.status}</Property>
            <Property name="Priority" prop="priority">{p.priority}</Property>
            <Property name="Location" prop="location">{p.location}</Property>
            <Property name="When" prop="when">{p.when}</Property>
            <Property name="Deadline" prop="deadline">{p.deadline}</Property>
            <Property name="Repeat" prop="repeat">{p.repeat}</Property>
            <Property name="Reminder" prop="reminder">{p.reminder}</Property>
            <Property name="Labels" prop="labels">{p.labels}</Property>
            <Property name="Snooze" prop="snooze">{p.snooze}</Property>
          </dl>
          <div data-console-meta className="mt-4 flex flex-col items-start gap-1.5 border-t border-border pt-3 text-[12px] text-subtle">
            {parts.meta}
          </div>
        </aside>
      </div>
    </div>
  );
}
